/**
 * Block 13 PR-G Phase 4C — Bottom-sheet выбора сохранённого КП.
 *
 * Async загружает `listSavedQuotes()` из Block 12 IDB при open. Используется
 * для двух flow:
 *   1. **Row-level «Из КП»** (SpecRowEditor name source / cost source) — выбор
 *      одного КП → callback с `SavedQuote` целиком (caller извлекает name
 *      или cost). Single-selection sheet.
 *   2. **Block-level «+ Добавить из КП»** (SpecBlockEditor) — выбор КП →
 *      callback с composition rows → reducer BULK_IMPORT_SPEC_ROWS_FROM_QUOTE.
 *
 * **5 P-021 states:**
 *   - `idle` — не возможно (sheet open ⇒ fetch fires).
 *   - `loading` — spinner-skeleton.
 *   - `success` — non-empty list (carded options).
 *   - `empty` — text «У вас пока нет сохранённых КП».
 *   - `error` — text «Не удалось загрузить КП. Попробуйте позже.»
 *
 * **A11y:** modal pattern — sheet captures clicks; Escape closes; option click
 * dispatches onSelect then closes; backdrop click closes без selection.
 *
 * Reference:
 *   - PRD §10.13.2 «Из КП» mode для row source и block-level bulk add.
 *   - Block 12 `listSavedQuotes()` API.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { listSavedQuotes, type SavedQuote } from '@/features/calculator/quote/state/savedQuotesIdb';

type LoadState =
  | { status: 'loading' }
  | { status: 'success'; quotes: SavedQuote[] }
  | { status: 'empty' }
  | { status: 'error' };

export interface ChoiceSheetForQuotesProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (quote: SavedQuote) => void;
  /** Title — варьируется per use-case: «Выбор КП», «Подтянуть стоимость из КП», etc. */
  title?: string;
  /** Subtitle описание action (e.g. «В строку попадёт только тип мебели»). */
  subtitle?: string;
}

export function ChoiceSheetForQuotes({
  isOpen,
  onClose,
  onSelect,
  title = 'Выберите КП',
  subtitle,
}: ChoiceSheetForQuotesProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setState({ status: 'loading' });
    listSavedQuotes()
      .then((quotes) => {
        if (cancelled) return;
        if (quotes.length === 0) {
          setState({ status: 'empty' });
        } else {
          // Newest first для UX (recent quotes user скорее всего хочет).
          const sorted = [...quotes].sort((a, b) => b.updatedAt - a.updatedAt);
          setState({ status: 'success', quotes: sorted });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (import.meta.env.DEV) {
          // P-009: log only err.name (DOMException может содержать record data).
          const errName = err instanceof Error ? err.name : 'unknown';
          console.error('[ChoiceSheetForQuotes] listSavedQuotes failed', errName);
        }
        setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function handleSelect(quote: SavedQuote) {
    onSelect(quote);
    onClose();
  }

  return (
    <div
      onClick={onClose}
      role="presentation"
      data-testid="quotes-sheet-backdrop"
      className="fixed inset-0 z-50 bg-black/50 flex flex-col justify-end items-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quotes-sheet-title"
        data-testid="quotes-sheet"
        className="w-full max-w-md bg-canvas rounded-t-3xl max-h-[80vh] overflow-y-auto px-4 pt-3 pb-8"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 id="quotes-sheet-title" className="font-bold text-ink text-xl m-0">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            data-testid="quotes-sheet-close"
            className="w-11 h-11 flex items-center justify-center rounded-md text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {subtitle ? (
          <p className="text-sm text-ink/60 mb-3" data-testid="quotes-sheet-subtitle">
            {subtitle}
          </p>
        ) : null}

        {state.status === 'loading' ? (
          <p
            role="status"
            data-testid="quotes-sheet-loading"
            className="text-ink/60 text-center py-8"
          >
            Загружаем сохранённые КП…
          </p>
        ) : null}

        {state.status === 'empty' ? (
          <p
            role="status"
            data-testid="quotes-sheet-empty"
            className="text-ink/60 text-center py-8"
          >
            У вас пока нет сохранённых КП. Создайте первое в Калькуляторе.
          </p>
        ) : null}

        {state.status === 'error' ? (
          <p
            role="alert"
            data-testid="quotes-sheet-error"
            className="text-destructive text-center py-8"
          >
            Не удалось загрузить КП. Попробуйте позже.
          </p>
        ) : null}

        {state.status === 'success' ? (
          <ul role="list" className="flex flex-col gap-2">
            {state.quotes.map((quote) => (
              <li key={quote.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(quote)}
                  data-testid={`quote-option-${quote.id}`}
                  className="w-full min-h-tap-chip-lg px-4 py-3 rounded-xl border-[1.5px] border-accent bg-card text-ink text-left focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90 flex flex-col gap-1"
                >
                  <span className="text-base font-semibold">{quote.name}</span>
                  {quote.state.furnitureType ? (
                    <span className="text-xs text-ink/60">{quote.state.furnitureType}</span>
                  ) : null}
                  {typeof quote.state.costWithMargin === 'number' &&
                  quote.state.costWithMargin > 0 ? (
                    <span className="text-sm font-semibold text-ink">
                      {quote.state.costWithMargin.toLocaleString('ru-RU')} ₸
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
